import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { responsiveSpacing } from '../../../themes/responsive';
import GroupingControl from './GroupingControl';
import FilterControl from './FilterControl';
import FilterPills from '../FilterPills';

/**
 * Main control bar for Technical Alerts table
 * Contains grouping, filtering and view mode controls
 * Extracted from TechnicalAlertsTable.jsx for better component separation
 */
const ControlBar = ({
  // State management
  state,
  
  // View mode props
  viewMode,
  onViewModeChange,
  
  // Grouping props
  groupingStrategy,
  groupingStrategyLabels,
  onGroupingStrategyChange,
  
  // Filter props
  filterData,
  filtersLoading,
  refetch,
  
  // Column configs
  columnConfigs,
  
  // Translation function
  t
}) => {
  const navigate = useNavigate();
  // Local dropdown state - isolated from card rendering
  const [showGroupingDropdown, setShowGroupingDropdown] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedFilterColumn, setSelectedFilterColumn] = useState(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close dropdown if clicking outside the grouping controls
      if (showGroupingDropdown && !event.target.closest('[data-grouping-control]')) {
        setShowGroupingDropdown(false);
      }
      // Close filter dropdown if clicking outside the filter controls
      if (showFilterDropdown && !event.target.closest('[data-filter-control]')) {
        setShowFilterDropdown(false);
        setSelectedFilterColumn(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showGroupingDropdown, showFilterDropdown]);

  // Toggle grouping dropdown
  const toggleGroupingDropdown = useCallback(() => {
    setShowGroupingDropdown(prev => !prev);
  }, []);

  // Toggle filter dropdown
  const toggleFilterDropdown = useCallback(() => {
    setShowFilterDropdown(prev => !prev);
    setSelectedFilterColumn(null);
  }, []);

  const goToAlertsManagement = useCallback(() => {
    navigate('/experiments', { state: { defaultTab: 'alerts' } });
  }, [navigate]);
  return (
    <Box sx={{
      px: responsiveSpacing.sm,
      py: 1,
      minHeight: { xs: 'auto', sm: '36px' },
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: { xs: 1, sm: 2 },
      flexWrap: 'wrap',
      position: 'sticky',
      top: 0,
      zIndex: 10,
      backgroundColor: 'white',
      transition: 'all 0.2s ease',
      boxShadow: 'none',
      borderBottom: 'none',
      marginTop: 0
    }}>
      {/* Left side - Filter Pills */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        {Object.keys(state.table.filteredInfo).some(key => state.table.filteredInfo[key]?.length > 0) && (
          <FilterPills
            filters={state.table.filteredInfo}
            onRemoveFilter={state.table.handleRemoveFilter}
            onClearAll={state.table.handleClearAllFilters}
            columnConfigs={columnConfigs}
            filterData={filterData}
          />
        )}
      </Box>

      {/* Right side - Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Group By and Filter controls - only show in card view */}
        {viewMode === 'cards' && (
          <>
            <GroupingControl
              groupingStrategy={groupingStrategy}
              groupingStrategyLabels={groupingStrategyLabels}
              showGroupingDropdown={showGroupingDropdown}
              onToggleGroupingDropdown={toggleGroupingDropdown}
              onGroupingStrategyChange={onGroupingStrategyChange}
              t={t}
            />

            <FilterControl
              state={state}
              showFilterDropdown={showFilterDropdown}
              selectedFilterColumn={selectedFilterColumn}
              onToggleFilterDropdown={toggleFilterDropdown}
              onSetSelectedFilterColumn={setSelectedFilterColumn}
              filterData={filterData}
              filtersLoading={filtersLoading}
              refetch={refetch}
              columnConfigs={columnConfigs}
              t={t}
            />
          </>
        )}
        
        {/* Estado Alertas button */}
        <Box
          onClick={goToAlertsManagement}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: '16px',
            py: '6px',
            cursor: 'pointer',
            borderRadius: '8px',
            backgroundColor: '#f8f9fa',
            border: 'none',
            transition: 'all 0.2s ease',
            fontSize: '14px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 500,
            color: '#64748b',
            '&:hover': {
              backgroundColor: '#f1f2f4'
            }
          }}
        >
          {t('trackAlertStatus')}
        </Box>
      </Box>
    </Box>
  );
};

export default ControlBar;
